import { PageSection } from "@components/layout/PageSection";
import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { ReactComponent as FooterEasyIcon } from "src/assets/illustrations/footer_easy_icon.svg";
import { ReactComponent as FooterImpactfulIcon } from "src/assets/illustrations/footer_impactful_icon.svg";
import { ReactComponent as FooterTrustedIcon } from "src/assets/illustrations/footer_trusted_icon.svg";
import { colorCssVars } from "src/theme/color";
import { cssForMediaSize, MediaSize } from "src/theme/mediaQueries";
import {
  horizontalStackCss,
  spacing,
  verticalStackCss,
} from "src/theme/spacing";
import { FontWeight, textSizeCss } from "src/theme/text";
import { useUserLayer } from "src/utility/abtesting";

// #region styles
/*
  Layout/typography duplicated from the `.footer-extended-text` /
  `.footer-extended-highlights` blocks in NewLandingPageFooter.tsx: those styles
  are class-scoped to that file's `StyledFooter`, so they cannot be reused from
  outside it. The footer's leaf-image background and section divider borders are
  intentionally not copied — this section sits on the plain dark page background.
*/
const StyledValuePropSection = styled.section`
  padding-top: 4.5rem;

  .value-prop-content {
    padding-right: ${spacing.l};
    padding-left: ${spacing.l};
  }

  .value-prop-text {
    text-align: center;
    ${verticalStackCss.m};
    align-items: center;
  }

  .value-prop-header {
    ${textSizeCss.xl};
    padding: 0 ${spacing.s};
  }

  .value-prop-copy {
    max-width: 600px;
  }

  .value-prop-highlights {
    margin-top: 3rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3.5rem;
    padding-bottom: 4rem;
  }

  .value-prop-highlight {
    max-width: 320px;
    text-align: center;
    ${verticalStackCss.xs};
    align-items: center;
  }

  .value-prop-highlight-heading {
    ${textSizeCss.l};
    font-weight: ${FontWeight.BOLD};
    color: var(${colorCssVars.accent.large});
    ${horizontalStackCss.xs};
    align-items: center;
  }

  ${cssForMediaSize({
    min: MediaSize.MEDIUM,
    css: css`
      padding-top: 6rem;

      .value-prop-content {
        padding-right: ${spacing.xxl};
        padding-left: ${spacing.xxl};
      }

      .value-prop-header {
        ${textSizeCss.xxl};
        padding: 0;
      }

      .value-prop-copy {
        ${textSizeCss.l};
        max-width: 640px;
      }

      .value-prop-highlights {
        flex-direction: row;
        justify-content: center;
        align-items: flex-start;
        padding-bottom: 6rem;
        gap: ${spacing.s};
      }
    `,
  })}

  ${cssForMediaSize({
    min: MediaSize.LARGE,
    css: css`
      .value-prop-highlights {
        gap: ${spacing.l};
        padding-bottom: 5.5rem;
      }

      .value-prop-highlight {
        padding: 0 ${spacing.l};
        max-width: 360px;
      }
    `,
  })}
`;
// #endregion

/**
 * Value proposition section shown between the hero and the donation feed for the
 * treatment of the `lp_val_1st` homepage section reorder experiment. In the
 * control this content is rendered inside `ExtendedFooter` instead.
 *
 * Every string is a layer parameter so copy can be tested without a deploy. Only
 * `vp_1st` is registered in the Statsig console; `layer.get()` serves the
 * defaults below for the copy params until a future copy test adds them there,
 * so these defaults are the source of truth.
 */
export function LandingValuePropSection() {
  const layer = useUserLayer("new_landing_page_layer", false);

  const heading = layer.get<string>(
    "vp_head",
    "No-Fee Online Fundraising Platform"
  );
  const subcopy = layer.get<string>(
    "vp_sub",
    "Every.org simplifies online giving for nonprofits and donors in order to accelerate social impact around the world."
  );

  const highlights = [
    {
      title: layer.get<string>("vp_easy_t", "Easy"),
      icon: <FooterEasyIcon />,
      description: layer.get<string>(
        "vp_easy_c",
        "Raise more with a modern fundraising solution that makes accepting all payment methods a breeze."
      ),
    },
    {
      title: layer.get<string>("vp_imp_t", "Impactful"),
      icon: <FooterImpactfulIcon />,
      description: layer.get<string>(
        "vp_imp_c",
        "Make every dollar count. No platform fees means more money goes directly to your cause."
      ),
    },
    {
      title: layer.get<string>("vp_trust_t", "Trusted"),
      icon: <FooterTrustedIcon />,
      description: layer.get<string>(
        "vp_trust_c",
        "Used by more than 8,000 nonprofits for secure and reliable donation processing."
      ),
    },
  ];

  return (
    <StyledValuePropSection data-tname="LandingPage-ValuePropSection">
      <PageSection contentClassName="value-prop-content">
        <div className="value-prop-text">
          <h2 className="value-prop-header">{heading}</h2>
          <p className="value-prop-copy">{subcopy}</p>
        </div>
        <div className="value-prop-highlights">
          {highlights.map(({ icon, title, description }) => (
            <div className="value-prop-highlight" key={title}>
              <div className="value-prop-highlight-heading">
                {icon}
                <span className="value-prop-highlight-title">{title}</span>
              </div>
              <p className="value-prop-highlight-desc">{description}</p>
            </div>
          ))}
        </div>
      </PageSection>
    </StyledValuePropSection>
  );
}
